import { useState, useEffect, useCallback, useRef } from "react";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";

const WRESTLING = [
  { src: "/assets/wrestling-champion-raise.jpg", alt: "A referee raising a professional wrestler's arm as the champion holds a title belt, black and white", credit: "TRED Photography", cls: "g-a", pos: "70% 50%" },
  { src: "/assets/wrestling-womens-strike.webp", alt: "Two women's professional wrestlers mid-match as one delivers a strike, referee watching, black and white", credit: "Heaven's Lariat Photography", cls: "g-b", pos: "left bottom" },
  { src: "/assets/wrestling-vertical-scream.webp", alt: "A professional wrestler low to the mat screaming toward the camera with an opponent stacked on his back, black and white", credit: "Heaven's Lariat Photography", cls: "g-tall", pos: "center 30%" },
  { src: "/assets/wrestling-airborne-beard.jpg", alt: "A bearded professional wrestler leaping off the ropes with arms raised above a seated opponent, black and white", credit: "TRED Photography", cls: "g-wide", pos: "center top" },
  { src: "/assets/wrestling-carry-hold.jpg", alt: "A professional wrestler holding an opponent horizontally across his body near the ropes, black and white", credit: "TRED Photography", cls: "g-wide", pos: "center 40%" },
  { src: "/assets/wrestling-wide-standoff.jpg", alt: "A wide cinematic shot of two professional wrestlers facing off in the ring beneath an exit sign, black and white", credit: "TRED Photography", cls: "g-full", pos: "center bottom" },
  { src: "/assets/wrestling-strike.jpg", alt: "Two professional wrestlers mid-strike in the ring, black and white", credit: "TRED Photography", cls: "g-wide", pos: "center top" },
  { src: "/assets/wrestling-mic.jpg", alt: "A man in a cap holding a microphone in a smoky wrestling ring, black and white", credit: "TRED Photography", cls: "g-sq", pos: "center 30%" },
];

const COMEDY = [
  { src: "/assets/comedy-laughing-ropes.jpg", alt: "A comedian laughing while leaning on the ring ropes during a live stand-up set, black and white", credit: "TRED Photography", cls: "g-a", pos: "right bottom" },
  { src: "/assets/comedy-vertical-hoodie.webp", alt: "A full-body shot of a comedian in a hoodie with an arm raised mid-set inside the ring, black and white", credit: "Heaven's Lariat Photography", cls: "g-tall", pos: "left bottom" },
  { src: "/assets/comedy-polo.jpg", alt: "A comedian in a jacket and cap performing with a microphone inside the ring, black and white", credit: "TRED Photography", cls: "g-sq", pos: "center 25%" },
  { src: "/assets/comedy-smile-jacket.jpg", alt: "A smiling comedian in a jacket over a white tee performing stand-up in the ring, black and white", credit: "TRED Photography", cls: "g-sq", pos: "center 25%" },
  { src: "/assets/comedy-glasses-cap.jpg", alt: "A comedian in glasses and a backward cap performing with a microphone in the ring, black and white", credit: "TRED Photography", cls: "g-wide", pos: "center 25%" },
];

const ALL = [...WRESTLING, ...COMEDY];

function Lightbox({ index, onClose, onPrev, onNext }) {
  const touchX = useRef(null);
  const item = ALL[index];

  const onKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    },
    [onClose, onPrev, onNext]
  );

  useEffect(() => {
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onKey]);

  return (
    <div
      className="lightbox"
      data-testid="lightbox"
      onClick={onClose}
      onTouchStart={(e) => (touchX.current = e.changedTouches[0].clientX)}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) (dx < 0 ? onNext : onPrev)();
        touchX.current = null;
      }}
    >
      <button className="lightbox-btn lightbox-close" aria-label="Close" data-testid="lightbox-close" onClick={(e) => { e.stopPropagation(); onClose(); }}>
        <X size={30} strokeWidth={1.5} />
      </button>
      <button className="lightbox-btn lightbox-prev" aria-label="Previous" data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); onPrev(); }}>
        <ArrowLeft size={28} strokeWidth={1.5} />
      </button>
      <img className="lightbox-img" src={item.src} alt={item.alt} data-testid="lightbox-image" onClick={(e) => e.stopPropagation()} />
      <button className="lightbox-btn lightbox-next" aria-label="Next" data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); onNext(); }}>
        <ArrowRight size={28} strokeWidth={1.5} />
      </button>
      <p className="lightbox-credit" data-testid="lightbox-credit">
        Photo — {item.credit}
      </p>
    </div>
  );
}

function Section({ id, eyebrow, title, items, offset, onOpen }) {
  return (
    <section data-testid={`gallery-${id}`} style={{ background: "var(--black)" }}>
      <div className="container-editorial section-pad">
        <Reveal>
          <p className="eyebrow" style={{ marginBottom: "1rem" }}>{eyebrow}</p>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="h-section" style={{ marginBottom: "clamp(2rem, 4vw, 3rem)" }}>{title}</h2>
        </Reveal>
        <div className="gallery-grid">
          {items.map((it, i) => (
            <button
              key={it.src}
              className={`g-tile ${it.cls}`}
              data-testid={`gallery-tile-${offset + i}`}
              onClick={() => onOpen(offset + i)}
              aria-label={`Open image: ${it.alt}`}
            >
              <img src={it.src} alt={it.alt} style={{ objectPosition: it.pos }} loading="lazy" />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function GalleryPage() {
  const [idx, setIdx] = useState(null);
  const close = () => setIdx(null);
  const prev = () => setIdx((v) => (v - 1 + ALL.length) % ALL.length);
  const next = () => setIdx((v) => (v + 1) % ALL.length);

  return (
    <Layout padTop>
      <Seo
        title="Gallery — Professional Wrestling & Stand-Up Comedy | Hustle Promotions"
        description="Photography from Hustle Promotions professional wrestling and stand-up comedy shows. Real performers, real events."
      />
      <section data-testid="gallery-hero" style={{ background: "var(--black)" }}>
        <div className="container-editorial" style={{ paddingTop: "clamp(2.5rem, 6vw, 4rem)", paddingBottom: "clamp(1rem, 2vw, 1.5rem)" }}>
          <Reveal>
            <p className="eyebrow" style={{ marginBottom: "1.25rem" }}>Gallery</p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="h-page" data-testid="gallery-h1" style={{ maxWidth: "16ch" }}>The Show, In Frame.</h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="body-lg" style={{ maxWidth: "48ch", marginTop: "1.5rem" }}>
              Photography from real Hustle Promotions professional wrestling and stand-up comedy shows.
            </p>
          </Reveal>
        </div>
      </section>

      <Section id="wrestling" eyebrow="Professional Wrestling" title="Wrestling" items={WRESTLING} offset={0} onOpen={setIdx} />
      <Section id="comedy" eyebrow="Stand-Up Comedy" title="Comedy" items={COMEDY} offset={WRESTLING.length} onOpen={setIdx} />

      {idx !== null && <Lightbox index={idx} onClose={close} onPrev={prev} onNext={next} />}
    </Layout>
  );
}
